import { useToast } from '@solfacil/girassol'
import { readonly, ref } from 'vue'

export type InstallationErrorStatus
  = | 'NOT_COMMISSIONED'
    | 'POWER_MISMATCH'
    | 'INSTALLATION_NOT_FOUND'
    | 'INVERTERS_NOT_FOUND'
    | 'TECHNICAL_FAILURE'
    | 'CREDENTIAL_NOT_FOUND'
    | 'CREDENTIAL_MISMATCH'
    | 'DUPLICATE_SERIAL_NUMBER'
    | 'END_CUSTOMER_CREDENTIAL'
    | 'NO_ENERGY_PRODUCED'
    | 'SERIAL_NUMBER_NOT_FOUND'
    | 'PRECONDITION_FAILED'
    | 'DUPLICATE_BRAND'
    | 'UNSUPPORTED_BRAND'
    | 'SERIAL_NUMBER_ALREADY_REGISTERED'
    | 'REJECTED_BY_CUSTOMER'

export interface InstallationAlertConfig {
  title: string
  text: string | string[]
}

export interface InstallationAlert {
  status: InstallationErrorStatus
  title: string
  text: string | string[]
}

export interface AlertParameters {
  [key: string]: string | number
}

const INSTALLATION_ERROR_ALERTS: Record<InstallationErrorStatus, InstallationAlertConfig> = {
  NOT_COMMISSIONED: {
    title: 'Inversor não comissionado',
    text: 'O inversor não foi comissionado corretamente. Verifique se a instalação foi finalizada e tente novamente.',
  },
  POWER_MISMATCH: {
    title: 'Potência informada não condiz com inversores validados',
    text: 'A potência total dos inversores informados não corresponde à potência contratada. Verifique as informações e submeta a instalação para reanálise.',
  },
  INSTALLATION_NOT_FOUND: {
    title: 'Número(s) de série não localizado(s) no seu portal de monitoramento',
    text: [
      'Não identificamos em sua conta no portal {{platformName}} uma planta de monitoramento que contenha um ou mais números de série informados nesta instalação. ',
      '',
      'Por favor, verifique se: ',
      '- A planta de monitoramento da instalação foi criada na sua conta {{accessUser}} no portal {{platformName}};',
      '- O(s) número(s) de série do(s) inversor(es) cadastrados na instalação aqui na Solfácil também foram cadastrados corretamente na planta.',
    ],
  },
  INVERTERS_NOT_FOUND: {
    title: 'Número(s) de série não localizado(s) no seu portal de monitoramento',
    text: [
      'Não identificamos em sua conta no portal {{platformName}} uma planta de monitoramento que contenha um ou mais números de série informados nesta instalação. ',
      '',
      'Por favor, verifique se: ',
      '- A planta de monitoramento da instalação foi criada na sua conta {{accessUser}} no portal {{platformName}};',
      '- O(s) número(s) de série do(s) inversor(es) cadastrados na instalação aqui na Solfácil também foram cadastrados corretamente na planta.',
    ],
  },
  TECHNICAL_FAILURE: {
    title: 'Falha técnica no sistema',
    text: 'Ocorreu uma falha técnica durante a verificação. Tente novamente em alguns instantes ou entre em contato com o suporte.',
  },
  CREDENTIAL_NOT_FOUND: {
    title: 'Login incorreto',
    text: 'Não há conta cadastrada na plataforma de monitoramento do fabricante {{platformName}}. Realize o cadastro na plataforma de monitoramento indicada ou reveja os dados informados. Atenção! A conta deve ser de administrador, além disso, a planta do cliente final deve estar cadastrada e possuir registro de geração acumulada',
  },
  CREDENTIAL_MISMATCH: {
    title: 'Credenciais incorretas',
    text: 'Por favor, verifique o e-mail e a senha cadastrados na plataforma {{platformName}} e tente novamente.',
  },
  END_CUSTOMER_CREDENTIAL: {
    title: 'Login não é administrador',
    text: 'Os dados de acesso informados não correspondem a uma conta ADMINISTRADOR/INSTALADOR. Insira os dados de acesso do perfil administrador e tente novamente. Atenção! Se necessário, realize o upgrade da sua conta par administrador e, após isso, submeta nova análise.',
  },
  DUPLICATE_SERIAL_NUMBER: {
    title: 'Número de série já informado anteriormente',
    text: 'O número de série do inversor informado foi detectado mais de uma vez: ou já foi enviado neste mesmo projeto (duplicado) ou em outro projeto. Verifique se as informações estão corretas e envie apenas 1 foto/informação por inversor antes de submeter nova análise.',
  },
  NO_ENERGY_PRODUCED: {
    title: 'Não foi identificada geração de energia no(s) inversor(es)',
    text: 'Não foi identificada geração de energia acumulada no(s) inversor(es) informado(s), indicando que o sistema não foi ligado. Pedimos que deixe o sistema funcionando/ligado  por aproximadamente 15min, até que haja registro de geraçao na plataforma de monitoramento. Após isso, submeta o projeto para nova análise. ',
  },
  SERIAL_NUMBER_NOT_FOUND: {
    title: 'Número(s) de série não localizado(s) no seu portal de monitoramento',
    text: [
      'Não identificamos em sua conta no portal {{platformName}} uma planta de monitoramento que contenha um ou mais números de série informados nesta instalação. ',
      '',
      'Por favor, verifique se: ',
      '- A planta de monitoramento da instalação foi criada na sua conta {{accessUser}} no portal {{platformName}};',
      '- O(s) número(s) de série do(s) inversor(es) cadastrados na instalação aqui na Solfácil também foram cadastrados corretamente na planta.',
    ],
  },
  PRECONDITION_FAILED: {
    title: 'Erro de pré condição',
    text: 'Não é possível combinar modos de entrada: os inversores atuais usam OCR, mas o novo é MANUAL',
  },
  DUPLICATE_BRAND: {
    title: 'Marcas duplicadas na instalação',
    text: 'Foi informada mais de uma marca de inversor na mesma instalação. Verifique os dados e informe inversores de uma única marca por instalação.',
  },
  UNSUPPORTED_BRAND: {
    title: 'Marca não suportada',
    text: 'A marca informada não é suportada para comissionamento automático. Verifique a marca selecionada ou entre em contato com o suporte.',
  },
  SERIAL_NUMBER_ALREADY_REGISTERED: {
    title: 'Número de série já informado anteriormente',
    text: 'O número de série do inversor informado foi detectado mais de uma vez: ou já foi enviado neste mesmo projeto (duplicado) ou em outro projeto. Verifique se as informações estão corretas e envie apenas 1 foto/informação por inversor antes de submeter nova análise.',
  },
  REJECTED_BY_CUSTOMER: {
    title: 'Instalação não reconhecida pelo cliente',
    text: 'O cliente informou que a instalação ainda não foi realizada. Confirme com seu cliente e submeta a instalação para reanálise.',
  },
} as const

const ERROR_STATUSES = Object.keys(INSTALLATION_ERROR_ALERTS) as InstallationErrorStatus[]

export const INSTALLATION_ERROR_STATUSES_NEEDING_ACCESS_USER: InstallationErrorStatus[] = [
  'INSTALLATION_NOT_FOUND',
  'INVERTERS_NOT_FOUND',
  'SERIAL_NUMBER_NOT_FOUND',
]

export function buildInstallationAlertParameters(
  status: InstallationErrorStatus,
  options: { platformName: string, accessUser?: string | null },
): AlertParameters {
  const parameters: AlertParameters = { platformName: options.platformName }

  if (
    INSTALLATION_ERROR_STATUSES_NEEDING_ACCESS_USER.includes(status)
    && options.accessUser
  ) {
    parameters.accessUser = options.accessUser
  }

  return parameters
}

const currentAlert = ref<InstallationAlert | null>(null)

export function isStatusAlertError(status: string): status is InstallationErrorStatus {
  return ERROR_STATUSES.includes(status as InstallationErrorStatus)
}

export function resolveInstallationErrorStatus(code?: string | null): InstallationErrorStatus {
  if (code && isStatusAlertError(code))
    return code

  return 'TECHNICAL_FAILURE'
}

export default function useInstallationAlerts() {
  const toast = useToast()

  function replacePlaceholders(text: string, parameters?: AlertParameters): string {
    let result = text

    if (parameters) {
      Object.entries(parameters).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`
        result = result.replace(new RegExp(placeholder, 'g'), String(value))
      })
    }

    result = result.replace(/\s*\{\{[^}]+\}\}\s*/g, '')
    result = result.replace(/\s+/g, ' ').trim()

    return result
  }

  function replacePlaceholdersInArray(textArray: string[], parameters?: AlertParameters): string[] {
    return textArray.map(line => replacePlaceholders(line, parameters))
  }

  function isAlertError(): boolean {
    return currentAlert.value !== null
  }

  function getErrorAlertConfig(status: InstallationErrorStatus, parameters?: AlertParameters): InstallationAlertConfig | undefined {
    const config = INSTALLATION_ERROR_ALERTS[status]
    if (!config) return undefined

    let text: string | string[]

    if (Array.isArray(config.text)) {
      text = replacePlaceholdersInArray(config.text, parameters)
    }
    else {
      text = replacePlaceholders(config.text, parameters)
    }

    return {
      title: replacePlaceholders(config.title, parameters),
      text,
    }
  }

  function showInstallationAlert(status: InstallationErrorStatus, parameters?: AlertParameters): void {
    const alertConfig = getErrorAlertConfig(status, parameters)

    if (alertConfig) {
      currentAlert.value = {
        title: alertConfig.title,
        text: alertConfig.text,
        status,
      }
    }
  }

  function showInstallationErrorToast(status: InstallationErrorStatus, parameters?: AlertParameters): void {
    const alertConfig = getErrorAlertConfig(status, parameters)

    if (alertConfig) {
      const description = Array.isArray(alertConfig.text) ? alertConfig.text.join('\n') : alertConfig.text
      toast.createToast({
        title: alertConfig.title,
        description,
        type: 'error',
      })
    }
  }

  function showSuccessToast(title: string, description?: string): void {
    toast.createToast({
      title,
      description: description ?? '',
      type: 'success',
    })
  }

  function showErrorToast(title: string, description?: string): void {
    toast.createToast({
      title,
      description: description ?? '',
      type: 'error',
    })
  }

  function hideInstallationAlert(): void {
    currentAlert.value = null
  }

  return {
    currentAlert: readonly(currentAlert),
    isStatusAlertError,
    isAlertError,
    getErrorAlertConfig,
    showInstallationAlert,
    showInstallationErrorToast,
    hideInstallationAlert,
    showSuccessToast,
    showErrorToast,
  }
}
